import type {
	MatchedEventsFlat,
	MatchedEventsRaw,
	MatchedEventsV2,
} from '../types'

/**
 * Pull a `string[]` of document ids out of a v2 `sources` / `reference_only`
 * entry value. Tolerates three backend shapes:
 * - `reference_only[key]`: a bare `string[]` of doc ids.
 * - `sources[key]`: an array of objects, each `{ doc_id, source }` — `.doc_id`
 *   is used, the `source` payload is ignored here.
 * - a legacy/extra-tolerant object carrying `{ doc_ids: string[] }`.
 *
 * For an array, each element is mapped: a `string` is used as-is; an object with
 * a string `doc_id` contributes `.doc_id`; anything else is ignored. Non-array,
 * non-`doc_ids` values → `[]`.
 *
 * @param value - A single per-key value from `sources` or `reference_only`.
 */
const extractDocIds = (value: unknown): Array<string> => {
	if (Array.isArray(value)) {
		const ids: Array<string> = []
		for (const element of value) {
			if (typeof element === 'string') {
				ids.push(element)
			} else if (
				element &&
				typeof element === 'object' &&
				'doc_id' in element &&
				typeof (element as { doc_id: unknown }).doc_id === 'string'
			) {
				ids.push((element as { doc_id: string }).doc_id)
			}
		}
		return ids
	}
	if (value && typeof value === 'object' && 'doc_ids' in value) {
		const ids = (value as { doc_ids: unknown }).doc_ids
		if (Array.isArray(ids)) {
			return ids.filter((v): v is string => typeof v === 'string')
		}
	}
	return []
}

/**
 * Type guard: is this the v2 *wrapped* shape? Discriminates on object **shape**
 * (presence of `sources` and/or `reference_only`), NOT on the numeric `version`
 * value — the real `case.json` reports `version: 1` for the wrapped structure,
 * so the numeric discriminator is unreliable.
 *
 * @param value - The raw `matched_events` payload.
 */
const isWrapped = (value: MatchedEventsRaw): value is MatchedEventsV2 => {
	if (!value || typeof value !== 'object') return false
	return 'sources' in value || 'reference_only' in value
}

/**
 * Normalize the case-detail `matched_events` payload to the flat shape every
 * consumer speaks: `{ 'connector_id:event_type': docId[] }`.
 *
 * - **v1 (flat record):** passed through (defensively coerced to `string[]`).
 * - **v2 (wrapped `{ version, sources, reference_only }`):** merges `sources`
 *   doc-ids and `reference_only` doc-ids per key, de-duplicating ids while
 *   preserving first-seen order.
 * - `null` / `undefined` → `null` (preserves the "no matched events" signal that
 *   `getCaseLogs` and `countLogs` already branch on).
 *
 * @param raw - The `matched_events` field exactly as the backend returns it.
 * @returns Flat `{ key: docId[] }`, or `null` when there are no matched events.
 */
export const normalizeMatchedEvents = (
	raw: MatchedEventsRaw,
): MatchedEventsFlat | null => {
	if (!raw) return null

	// v1: plain record passthrough (coerce values to string[])
	if (!isWrapped(raw)) {
		const flat: MatchedEventsFlat = {}
		for (const [key, ids] of Object.entries(raw)) {
			if (!key) continue
			flat[key] = Array.isArray(ids)
				? ids.filter((v): v is string => typeof v === 'string')
				: []
		}
		return Object.keys(flat).length > 0 ? flat : null
	}

	// v2: merge sources + reference_only into the flat shape, de-duping ids
	const merged = new Map<string, Array<string>>()

	const ingest = (bucket: Record<string, unknown> | undefined | null) => {
		if (!bucket) return
		for (const [key, value] of Object.entries(bucket)) {
			if (!key) continue
			const ids = extractDocIds(value)
			if (ids.length === 0 && !merged.has(key)) {
				merged.set(key, [])
				continue
			}
			const existing = merged.get(key) ?? []
			const seen = new Set(existing)
			for (const id of ids) {
				if (!seen.has(id)) {
					existing.push(id)
					seen.add(id)
				}
			}
			merged.set(key, existing)
		}
	}

	ingest(raw.sources)
	ingest(raw.reference_only)

	if (merged.size === 0) return null
	return Object.fromEntries(merged)
}
